import { useContext, useEffect, useRef, useState } from 'react';
import { useChatbot } from '@libertify.frontend/chatbot/index';
import { AgentResponse } from '@libertify.frontend/chatbot/types';
import { CHAT_STATE, RECONNECT_BACKOFF_COUNT } from './constants';
import { ChatState, Sessions } from './types';
import { AppContext } from '../../contexts/AppContext';
import {
  useExperienceActions,
  useExperienceConfig,
} from '../../contexts/ExperienceContext';
import {
  closeOpenReference,
  deleteOldSessions,
  getChatState,
  submitButtonLabel,
  updateChatState,
} from './utils';
import {
  ChatMessage,
  Layout,
  MediaProjectInstanceResponse,
  MessageOrigin,
} from '../../types';
import styled, { ThemeProvider } from 'styled-components';
import { Flex, notification, theme } from 'antd';
import { useAntTheme } from '../../hooks/useAntTheme';
import { ChatStatus } from './ChatStatus';
import ScrollToBottom from 'react-scroll-to-bottom';
import { Message as MessageComponent } from './Message';
import { PromptForm } from './PromptForm';
import styles from './ChatBot.module.css';
import { ChatStats } from './ChatStats';
import { hasChatAnswers } from '../../helpers';
import './styles.css';
import { useTranslation } from 'react-i18next';
import { Disclaimer } from './components/Disclaimer';
import { TypingIndicator } from './components/TypingIndicator';

export const ChatBot = ({
  onBeforeQuery,
  organizationId,
  firstQuery,
  nextQuery,
  open,
  showSources,
  mainDocumentId,
  publicationDocuments,
  publication,
  setSuggestions,
  previewAccessToken,
  isLegacy,
}: {
  onBeforeQuery?: () => void;
  documentIds: string[];
  organizationId: string;
  firstQuery?: string;
  nextQuery?: string;
  open: boolean;
  layout?: Layout;
  showSources?: (coordinates: MessageOrigin) => void;
  mainDocumentId?: string;
  publicationDocuments?: MediaProjectInstanceResponse['documents'];
  publication: MediaProjectInstanceResponse;
  suggestions?: string[];
  setSuggestions: (suggestions: string[]) => void;
  previewAccessToken?: string | null;
  isLegacy?: boolean;
}) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { isMobile } = useAntTheme();
  const [reconnectCount, setReconnectCount] = useState(0);
  const hasMounted = useRef(false);
  const { identifiers } = useContext(AppContext);
  const { projectId, publicationId, organizationSlug, instanceSlug } =
    identifiers;
  const { getAuthCredentials } = useExperienceActions();
  const { chatbotUrl, appPrefix } = useExperienceConfig();
  const sessionKey = `${projectId}:${publicationId}`;
  const state = useRef<ChatState>(getChatState(sessionKey) as ChatState);
  const [api, contextHolder] = notification.useNotification();
  const socketUrl = chatbotUrl ?? '';
  const session_id = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (hasMounted.current) return;

    session_id.current = state.current?.sessions?.[sessionKey]?.session_id;

    if (state.current?.sessions?.[sessionKey]) {
      deleteOldSessions(state.current.sessions as Sessions);
      sessionStorage.setItem(CHAT_STATE, JSON.stringify(state.current));
    }
    // when refresh the page, isOpen should be false, like common chat widgets
    closeOpenReference(sessionKey);
    updateChatState(state, sessionKey);
    hasMounted.current = true;
  }, [sessionKey]);

  const saveSession = (sessionId: string) => {
    const newState: ChatState = {
      ...state.current,
      isOpen: true,
      sessions: {
        ...state.current?.sessions,
        [sessionKey]: {
          session_id: sessionId,
          timestamp: Date.now(),
        },
      },
    };
    sessionStorage.setItem(CHAT_STATE, JSON.stringify(newState));
    updateChatState(state, sessionKey);
  };

  const {
    isReady,
    sendQuery,
    messageHistory,
    requestTranscript,
    isTranscriptRequestAcknowledged,
    connectionStatus,
    connect,
    isConnected,
    isBusy,
    sendJsonMessage,
  } = useChatbot<AgentResponse>(socketUrl, {
    firstQuery,
    // Keep the conversation across chat close/reopen — the panel unmounts on
    // close, which would otherwise wipe the visible history.
    historyKey: sessionKey,
    shouldReconnect: (_closeEvent: CloseEvent) => {
      if (reconnectCount < RECONNECT_BACKOFF_COUNT) {
        setReconnectCount((prev) => prev + 1);
        return true;
      }
      return false;
    },

    onOpen: async () => {
      const {
        bearerToken,
        password,
        documentIds: credentialDocIds,
      } = await getAuthCredentials({
        projectId,
        publicationId,
        organizationSlug,
        instanceSlug,
        cookiePrefix: appPrefix,
      });
      const data = {
        action: 'OpenSocket',
        context: {
          organization_id: 'libertify',
          session_id: session_id.current,
          organization_context: {
            organization_id: organizationId,
            ...(credentialDocIds?.length
              ? { document_ids: credentialDocIds }
              : {
                  project_id: publication?.project_id,
                  publication_id: publication?.instance_id,
                }),
            bearer_token: bearerToken,
            password: previewAccessToken ?? password,
          },
        },
      };
      sendJsonMessage(data);
    },
    onMessage: (data: AgentResponse) => {
      const d = data as AgentResponse & {
        session_id?: string;
        metadata?: { suggested_questions?: string[] };
      };
      if (d?.session_id) saveSession(d.session_id);
      if (d.metadata?.suggested_questions)
        setSuggestions(d.metadata.suggested_questions);
    },
    onClose: (event: CloseEvent) =>
      console.log('❌ WebSocket disconnected', event),
    onError: (event: Event) => console.error('❌ Error:', event),
  });

  // Connect when opening the chatbot via click on input field
  useEffect(() => {
    if (open && !state.current?.isOpen) {
      connect();
    }
  }, [open, isConnected, connect]);

  // Derive loading state from isReady: shows loading until the welcome message arrives
  // Don't show loading if connection failed (status is 'Closed')
  const loading =
    !isReady && messageHistory.length === 0 && connectionStatus !== 'Closed';

  // When open and there is a next query,
  // append it to the message history
  useEffect(() => {
    if (isReady && nextQuery) {
      sendQuery(nextQuery);
    }
  }, [isReady, nextQuery]);

  useEffect(() => {
    if (isTranscriptRequestAcknowledged) {
      api['success']({
        message: t('summaryRequestSuccessful'),
        duration: 3,
      });
    }
  }, [isTranscriptRequestAcknowledged, api]);

  const tooltipParams = {
    connectionStatus: connectionStatus,
    isConnected,
    isMobile,
  };

  return (
    <ThemeProvider theme={theme as any}>
      {contextHolder}
      <div className="chatbot-container">
        {!isMobile && hasChatAnswers(publication) && (
          <ChatStats
            messages={messageHistory as ChatMessage[]}
            requestTranscript={requestTranscript}
            canRequestTranscript={!isTranscriptRequestAcknowledged}
          />
        )}
        <div className="chatbot-scroll-area">
          <ScrollToBottom
            followButtonClassName={'scrollToBottomButton'}
            className={styles.chatbox}
          >
            <Flex vertical gap={10} style={{ marginBottom: 16 }}>
              <Disclaimer />
              {loading && <TypingIndicator />}
              {messageHistory.map((message: any, index: number) => (
                <MessageComponent
                  key={message.queryId ?? index}
                  publication={publication}
                  mainDocumentId={mainDocumentId}
                  publicationDocuments={publicationDocuments}
                  message={message}
                  sendQuery={(query) => {
                    if (onBeforeQuery) onBeforeQuery();
                    sendQuery(query);
                  }}
                  displaySource={(source: MessageOrigin) =>
                    showSources?.(source)
                  }
                />
              ))}
            </Flex>
          </ScrollToBottom>
        </div>
        <StyledPromptForm
          $isMobile={isMobile}
          $isLegacy={isLegacy}
          className="chat-input-row"
        >
          {isLegacy && <ChatStatus {...tooltipParams} />}
          <PromptForm
            inputPosition={'chat'}
            disabled={isBusy || !isConnected}
            onChange={sendQuery}
            value=""
            submitButtonLabel={isLegacy ? undefined : submitButtonLabel()}
            autoFocus
          />
        </StyledPromptForm>
      </div>
      {!isLegacy && <ChatStatus {...tooltipParams} />}
    </ThemeProvider>
  );
};

const StyledPromptForm = styled.div<{
  $isMobile: boolean;
  $isLegacy?: boolean;
}>`
  position: sticky;
  bottom: ${(props) => (props.$isLegacy ? '30px' : '0')};
  width: 100%;
  flex-shrink: 0;
`;
